import { ModelTranslationService } from '@/core/transloco/model-translation.service';
import { TranslocoPageRouteSyncService } from '@/core/transloco/transloco-loader';
import { SortOrderEnum } from '@/services/ui/table/base-table.service';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  ElementRef,
  HostBinding,
  Injector,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import {
  MatMenu,
  MatMenuContent,
  MatMenuTrigger,
} from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorIntl,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton } from '@angular/material/radio';
import {
  MatSort,
  MatSortHeader,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import {
  MatHeaderCellDef,
  MatHeaderRowDef,
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { MatRowDetailComponent } from '.';
import { ButtonComponent } from '../custom-elements/button/button.component';
import { RowPrintButtonComponent } from '../print-template-dialog/row-print-button/row-print-button.component';
import { ColumnFilterComponent } from './column-filter/column-filter.component';
import { MatTablePaginatorIntl } from './mat-paginator-intl.service';
import { MatTableService } from './mat-table.service';
import { MatTableColumn, MatTableFilter } from './mat-table.types';
import { ResizableColumnDirective } from './resizable-column/resizable-column.directive';
import {
  TABLE_ROW_HEIGHT_PX,
  TABLE_VIRTUAL_BUFFER_ROWS,
  computeExpandedRowDetailMaxHeightPx,
  computeTableVirtualWindow,
} from './table-viewport-virtualization';

/** `data-column-id` selector; ids may contain `.`, spaces, or `()`. */
export function columnIdDomSelector(columnId: string): string {
  return `[data-column-id="${CSS.escape(columnId)}"]`;
}

@Component({
  selector: 'app-mat-table-base',
  imports: [
    MatTableModule,
    NgClass,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatHeaderCellDef,
    MatHeaderRowDef,
    MatSortHeader,
    MatSort,
    MatPaginator,
    MatCheckboxModule,
    MatProgressSpinner,
    NgStyle,
    MatIcon,
    MatCheckboxModule,
    MatMenu,
    MatMenuContent,
    ColumnFilterComponent,
    RowPrintButtonComponent,
    TranslocoPipe,
    ResizableColumnDirective,
    ButtonComponent,
    DragDropModule,
    CdkScrollable,
    CdkDropList,
    CdkDrag,
    MatRadioButton,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: MatTablePaginatorIntl,
    },
  ],
  templateUrl: './mat-table-base.component.html',
  styleUrl: './mat-table-base.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatTableBaseComponent<T = any>
  implements AfterViewInit, OnDestroy
{
  // Input for the table service
  readonly table = input.required<MatTableService<T>>();

  // ViewChild references
  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatTable) matTable?: MatTable<T>;
  @ViewChild(MatPaginator, { read: ElementRef })
  paginatorElement?: ElementRef<HTMLElement>;

  readonly paginatorSelectConfig = {
    panelClass: 'mat-table-paginator-page-size-panel',
  };

  private paginatorPageSizeTooltipCleanup?: () => void;
  private paginatorTriggerTooltip?: MatTooltip;
  private readonly paginatorOptionTooltips = new Map<HTMLElement, MatTooltip>();

  @ViewChildren('rowDetailContainer', { read: ViewContainerRef })
  rowDetailContainers?: QueryList<ViewContainerRef>;

  /**
   * Each `.row-detail-float` overlay; observed for height changes so the
   * empty spacer `<td>` inside the table can match its height (pushing
   * subsequent rows down by the right amount).
   */
  @ViewChildren('rowDetailFloat', { read: ElementRef })
  rowDetailFloats?: QueryList<ElementRef<HTMLElement>>;

  @ViewChild('tableContainer', { static: false })
  tableContainerRef?: { nativeElement: HTMLElement };

  @ViewChild('tableWrapper', { static: false })
  tableWrapperRef?: { nativeElement: HTMLElement };

  // Reference to table container for scoped queries
  private get tableContainer(): HTMLElement | undefined {
    return this.tableContainerRef?.nativeElement;
  }

  // Reference to table wrapper for scroll reset
  private get tableWrapper(): HTMLElement | undefined {
    return this.tableWrapperRef?.nativeElement;
  }

  // Track detail component instances by row
  private detailComponents = new Map<
    T,
    ComponentRef<MatRowDetailComponent<T>>
  >();

  // Track rows pending component creation (after view update)
  private pendingRowCreations = new Set<T>();

  // Track rows that are closing (for animation) - keeps row visible during close animation
  private closingRows = signal<Set<T>>(new Set());

  // Track rows that are mid-expansion: the float has just been rendered into the
  // DOM but we want the wrapper to start at `max-height: 0` (no
  // `.row-detail-wrapper-expanded` class) so the CSS transition fires when we
  // remove it from this set on the next animation frame. Without this, the
  // wrapper renders with the expanded class already applied and skips the
  // open animation.
  private expandingRows = signal<Set<T>>(new Set());

  // Track heights of closing rows for animation
  private closingRowHeights = new Map<T, number>();

  // Track column widths for resizing
  private columnWidths = new Map<string, number>();
  private initialResizeWidths = new Map<string, number>(); // Track initial widths during resize
  private allColumnInitialWidths = new Map<string, number>(); // Store all column widths at resize start
  /** While true, skip fill-container redistribution and [style.width] binding fights drag. */
  private columnResizeActive = false;
  /**
   * After the user resizes a column once, never run full enforce (proportional fill + cell rewrites);
   * that was shifting sibling columns and the handle. Only sync the table shell from the map.
   */
  private userColumnLayoutLocked = false;
  private resizeObserver?: ResizeObserver;

  // Row-detail floating overlay tracking. The overlays live OUTSIDE the
  // horizontal scroll container, so horizontal scrolling cannot lag them.
  // - `detailFloatHeights`: measured height of each float; bound back into the
  //   spacer `<td>` so it pushes subsequent rows down by the same amount.
  // - `detailFloatTops`: top offset of each float relative to `.table-wrapper`
  //   scroll content; matches the spacer cell's vertical position.
  private detailFloatHeights = signal(new Map<string, number>());
  private detailFloatTops = signal(new Map<string, number>());
  private detailFloatResizeObserver?: ResizeObserver;
  private detailFloatTopRafId: number | null = null;
  private readonly viewportScrollTop = signal(0);
  private readonly viewportHeight = signal(0);
  /** Last full in-memory list (`data()`), used so scroll-window updates do not collapse details. */
  private lastFullData: T[] = [];
  private readonly onTableWrapperScroll = (): void => {
    const wrapper = this.tableWrapper;
    if (!wrapper) return;
    const next = wrapper.scrollTop;
    if (next !== this.viewportScrollTop()) {
      this.viewportScrollTop.set(next);
    }
  };

  private cdr = inject(ChangeDetectorRef);
  private transloco = inject(TranslocoService);
  private readonly modelTranslation = inject(ModelTranslationService);
  private readonly pageI18n = inject(TranslocoPageRouteSyncService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  /**
   * Translate a column header key using the global namespace (no scope).
   *
   * Column label keys like "shipping-events.table.columns.internalCode" live in
   * scoped JSON files. Once that scope loads, Transloco merges it into the active
   * language's global translations, so calling translate(key) with NO scope argument
   * finds them.
   */
  translateHeader(label: string | undefined | null): string {
    if (!label) return '';
    return this.modelTranslation.translateColumnHeader(label);
  }

  // Use MatTableDataSource to prevent table re-initialization
  // This keeps the same instance and only updates data, preventing DOM removal
  readonly dataSource = new MatTableDataSource<T>([]);

  private static readonly FALLBACK_APPEARANCE = {
    showRowNumbers: false,
    showRowHover: true,
    striped: false,
    bordered: true,
    dense: false,
    heightMode: 'fill-parent' as const,
    fillContainer: true,
  };

  // Computed values from service
  readonly columns = computed(() => this.table()?.columns() ?? []);
  readonly layoutDisplayedColumns = computed(() => {
    this.table()?.columnLayoutEpoch();
    return this.table()?.displayedColumns() ?? [];
  });
  readonly orderedColumns = computed(() => {
    this.table()?.columnLayoutEpoch();
    return this.columns();
  });
  /** Column id for MatSort arrow; maps query.sort_field back to header id. */
  readonly matSortActive = computed(() => {
    const table = this.table();
    if (!table) return '';
    const sortField = table.query().sort_field;
    if (!sortField) return '';
    const column = this.columns().find(col => {
      const originalId = table.getOriginalColumnId(col.id);
      return col.sortField === sortField || originalId === sortField;
    });
    return column?.id ?? '';
  });
  readonly matSortDirection = computed((): SortDirection => {
    const order = this.table()?.query().sort_order;
    return order === SortOrderEnum.ASC || order === SortOrderEnum.DESC
      ? order
      : '';
  });
  readonly data = computed(() => this.table()?.data() ?? []);
  /**
   * Rows that can appear in the table body.
   * When virtualizing, tree-collapsed children are omitted from the DOM.
   * Otherwise keep the full list and hide them with `[hidden]` as before.
   */
  readonly sourceRows = computed(() => {
    if (!this.virtualizationEnabled()) {
      return this.data();
    }
    return this.table()?.visibleData() ?? this.data();
  });
  readonly isLoading = computed(() => this.table()?.isLoading() ?? false);
  readonly totalCount = computed(() => this.table()?.totalCount() ?? 0);
  readonly pagination = computed(
    () =>
      this.table()?.pagination() ?? {
        enabled: false,
        pageSize: 10,
        pageSizeOptions: [],
        showFirstLastButtons: true,
      }
  );
  readonly showPagination = computed(
    () => this.pagination().enabled && this.data().length !== 0
  );
  readonly rowExpansion = computed(
    () =>
      this.table()?.rowExpansion() ?? {
        enabled: false,
        expandOnRowClick: true,
        multipleExpanded: false,
        hideExpandColumn: false,
      }
  );
  readonly childRows = computed(
    () =>
      this.table()?.childRows() ?? {
        enabled: false,
        getChildren: () => undefined,
        isParent: () => false,
        isChild: () => false,
      }
  );
  readonly selection = computed(
    () =>
      this.table()?.selection() ?? {
        enabled: false,
        mode: 'multiple' as const,
      }
  );
  readonly actions = computed(
    () =>
      this.table()?.actions() ?? {
        enabled: false,
        actions: [],
        sticky: false,
      }
  );
  readonly showActions = computed(
    () => this.actions().enabled && this.data().length !== 0
  );
  /** Show actions header cell when there is a header template (e.g. Add) even with no rows. */
  readonly showActionsHeader = computed(() => {
    const a = this.actions();
    if (!a.enabled) return false;
    if (a.headerTemplate) return true;
    return this.data().length > 0;
  });
  readonly appearance = computed(
    () =>
      this.table()?.appearance() ?? MatTableBaseComponent.FALLBACK_APPEARANCE
  );
  readonly dragDrop = computed(
    () =>
      this.table()?.dragDrop() ?? {
        enabled: false,
      }
  );
  readonly columnReorder = computed(() => this.table()?.columnReorder());

  /** While a column header is being dragged. */
  readonly columnDragActive = signal(false);
  readonly columnDragSourceIndex = signal<number | null>(null);
  readonly columnDragOverIndex = signal<number | null>(null);
  readonly columnDragGhost = signal<{
    label: string;
    x: number;
    y: number;
  } | null>(null);
  /** Original column id highlighted briefly after a successful drop. */
  readonly columnDropHighlightId = signal<string | null>(null);
  /** Drives a short table-wide reorder animation. */
  readonly columnReorderAnimating = signal(false);

  /** Id of the column currently being dragged (null when not dragging). */
  readonly columnDragSourceId = computed(() => {
    const idx = this.columnDragSourceIndex();
    if (idx == null) return null;
    return this.orderedColumns()[idx]?.id ?? null;
  });

  /**
   * Live-preview column order during drag: source column is moved to the
   * hover position so the table reorders in real time as the user drags.
   */
  readonly dragPreviewColumns = computed(() => {
    const cols = this.orderedColumns();
    if (!this.columnDragActive()) return cols;
    const source = this.columnDragSourceIndex();
    const over = this.columnDragOverIndex();
    if (source == null || over == null || source === over) return cols;
    const result = [...cols];
    const [moved] = result.splice(source, 1);
    result.splice(over, 0, moved);
    return result;
  });

  /**
   * Displayed column-id array with data columns in preview order, keeping
   * fixed columns (select, expand, actions, …) at their original positions.
   */
  readonly dragPreviewDisplayedColumns = computed(() => {
    const displayed = this.layoutDisplayedColumns();
    if (!this.columnDragActive()) return displayed;
    const source = this.columnDragSourceIndex();
    const over = this.columnDragOverIndex();
    if (source == null || over == null || source === over) return displayed;

    const FIXED = new Set([
      'select',
      'rowNumber',
      'expand',
      'childToggle',
      'actions',
      'expandedDetail',
    ]);
    const prefix: string[] = [];
    const dataIds: string[] = [];
    const suffix: string[] = [];
    let seenData = false;

    for (const id of displayed) {
      if (!FIXED.has(id)) {
        seenData = true;
        dataIds.push(id);
      } else if (!seenData) {
        prefix.push(id);
      } else {
        suffix.push(id);
      }
    }

    const reordered = [...dataIds];
    const [moved] = reordered.splice(source, 1);
    reordered.splice(over, 0, moved);
    return [...prefix, ...reordered, ...suffix];
  });

  private columnDragPointerId: number | null = null;
  private columnDragPointerCleanup: (() => void) | null = null;
  private columnDragScrollRaf: number | null = null;
  /** Current auto-scroll speed (px/frame); refreshed on pointermove. */
  private columnDragAutoScrollSpeed = 0;
  /** Last pointer X during a column drag; drop target is re-resolved from it after scrolls. */
  private columnDragLastClientX: number | null = null;
  /** Wrapper scrollLeft at drag-start; frozen header rects are shifted by the scroll delta. */
  private columnDragScrollLeftAtStart = 0;
  /** Frozen column header positions captured at drag-start; used instead of
   *  live DOM reads so that preview rearrangements don't feed back into the
   *  index-resolution and cause oscillation. */
  private columnDragOriginalRects: { left: number; right: number }[] = [];

  /**
   * Rows that need a floating row-detail overlay rendered (open or animating
   * closed). Driven by `sourceRows()`, `closingRows()`, and `table().isRowExpanded`.
   */
  readonly virtualizationEnabled = computed(() => {
    if (this.dragDrop().enabled) return false;
    return (this.appearance().heightMode ?? 'fill-parent') === 'fill-parent';
  });

  readonly detailFloatRows = computed(() => {
    return this.sourceRows().filter(row => this.isRowExpanded(row));
  });

  private readonly visibleIndexByRow = computed(() => {
    const map = new Map<T, number>();
    this.sourceRows().forEach((row, index) => map.set(row, index));
    return map;
  });

  private readonly expandedRowExtras = computed(() => {
    const indexByRow = this.visibleIndexByRow();
    const extras = [];
    for (const row of this.detailFloatRows()) {
      const afterIndex = indexByRow.get(row);
      if (afterIndex === undefined) continue;
      const height = this.getDetailSpacerHeight(row);
      if (height > 0) {
        extras.push({ afterIndex, height });
      }
    }
    return extras;
  });

  readonly virtualWindow = computed(() => {
    const rows = this.sourceRows();
    if (!this.virtualizationEnabled()) {
      return {
        startIndex: 0,
        endIndex: rows.length,
        topPad: 0,
        bottomPad: 0,
      };
    }
    return computeTableVirtualWindow({
      rowCount: rows.length,
      rowHeight: TABLE_ROW_HEIGHT_PX,
      viewportHeight: this.viewportHeight(),
      scrollTop: this.viewportScrollTop(),
      bufferRows: TABLE_VIRTUAL_BUFFER_ROWS,
      extras: this.expandedRowExtras(),
    });
  });

  readonly renderedRows = computed(() => {
    const rows = this.sourceRows();
    if (!this.virtualizationEnabled()) return rows;
    const { startIndex, endIndex } = this.virtualWindow();
    return rows.slice(startIndex, endIndex);
  });

  private readonly renderedRowSet = computed(
    () => new Set<T>(this.renderedRows())
  );

  readonly virtualTopPad = computed(() => this.virtualWindow().topPad);
  readonly virtualBottomPad = computed(() => this.virtualWindow().bottomPad);

  /** Pixel top offset of `.row-detail-float` for `row` (relative to `.table-wrapper`). */
  getDetailFloatTop(row: T): number {
    return this.detailFloatTops().get(this.getRowId(row)) ?? 0;
  }

  /** Pixel height to apply to the in-table spacer `<td>` for `row`. */
  getDetailSpacerHeight(row: T): number {
    return this.detailFloatHeights().get(this.getRowId(row)) ?? 0;
  }

  /** Reliable for styles; :host-context(.row-detail-container) is brittle with encapsulation. */
  @HostBinding('class.mat-table-in-row-detail')
  protected get matTableInRowDetailHostClass(): boolean {
    return this.isTableInsideRowDetail();
  }

  // Effect to update MatTableDataSource when data signal changes
  // CRITICAL: Mutate array in place instead of replacing to prevent DOM removal
  // This prevents Material Table from removing the table from DOM
  protected dataUpdateEffect = effect(() => {
    const newData = this.data();
    const rendered = this.renderedRows();
    const currentData = this.dataSource.data;
    const loading = this.isLoading();

    // Only preserve existing data during loading to prevent flicker
    // If not loading and new data is empty, clear to show empty state
    if (newData.length === 0 && this.lastFullData.length > 0) {
      if (loading) {
        // Keep existing data visible during fetch - don't update dataSource
        return;
      } else {
        // Not loading and no data - clear to show empty state
        this.lastFullData = [];
        currentData.length = 0;
        (this.dataSource as any)._updateChangeSubscription?.();
        return;
      }
    }

    // Mutate the existing array in place instead of replacing it
    // This preserves the array reference, preventing Material Table from re-initializing
    if (newData.length > 0) {
      // Collapse/destroy only when the full in-memory list identity changes.
      // Viewport windowing changes `rendered` without changing `data()`.
      const sameFullIdentity =
        newData.length === this.lastFullData.length &&
        newData.every((row, index) => row === this.lastFullData[index]);
      const sameRendered =
        rendered.length === currentData.length &&
        rendered.every((row, index) => row === currentData[index]);

      if (sameFullIdentity && sameRendered) {
        // Polling replaces the outer array with the same row objects — refresh
        // cells. Scroll events that do not move the window must not CD.
        if (newData !== this.lastFullData) {
          this.lastFullData = newData;
          (this.dataSource as any)._updateChangeSubscription?.();
          this.cdr.markForCheck();
        }
        return;
      }

      if (!sameFullIdentity) {
        this.lastFullData = newData;

        const expandedRows = Array.from(this.detailComponents.keys());
        this.table()?.collapseAll();
        expandedRows.forEach(row => {
          this.destroyDetailComponent(row);
        });

        if (this.rowDetailContainers) {
          this.rowDetailContainers.forEach(container => {
            container.clear();
          });
        }

        this.pendingRowCreations.clear();
      }

      if (!sameRendered) {
        currentData.length = 0;
        currentData.push(...rendered);
        (this.dataSource as any)._updateChangeSubscription?.();
      }

      if (!sameFullIdentity) {
        this.resetScrollPosition();
        this.cdr.detectChanges();
        setTimeout(() => {
          this.enforceAllColumnMinWidths();
        }, 0);
      } else {
        this.cdr.markForCheck();
        this.matTable?.renderRows();
        this.scheduleDetailFloatTopRecompute();
      }
    } else if (newData.length === 0 && currentData.length === 0) {
      // Only set empty array if both are empty (initial state)
      this.dataSource.data = [];
    }
  });

  ngAfterViewInit(): void {
    this.transloco.langChanges$
      .pipe(
        startWith(this.transloco.getActiveLang()),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.pageI18n.ensureKnownComponentBundle('mat-table'))
      )
      .subscribe(() => this.cdr.markForCheck());

    if (!this.table()) {
      return;
    }

    const table = this.table();
    // Parent may have pre-fetched before mounting (e.g. catalogue DSO projection).
    if (table.data().length === 0 && !table.isLoading()) {
      table.fetch();
    }

    // Listen to changes in rowDetailContainers to create pending components
    this.rowDetailContainers?.changes.subscribe(() => {
      this.createPendingDetailComponents();
    });

    // Whenever the set of `.row-detail-float` overlays changes (rows expanded
    // or collapsed), reattach the height ResizeObserver and recompute tops.
    this.rowDetailFloats?.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setupDetailFloatObservers();
        this.scheduleDetailFloatTopRecompute();
      });

    this.syncViewportMetrics();
    this.bindViewportScroll();

    // Enforce minWidths after view init
    setTimeout(() => {
      this.enforceAllColumnMinWidths();
      this.setupResizeObserver();
      this.syncViewportMetrics();
      this.bindViewportScroll();
      this.setupDetailFloatObservers();
      this.scheduleDetailFloatTopRecompute();
      this.setupPaginatorPageSizeTooltips();
      this.cdr.markForCheck();
    }, 100);

    // When any translation scope finishes loading, force re-render so column
    // headers resolve from the global translations cache.
    // translateHeader() calls transloco.translate(label) with NO scope, which
    // reads the global translations. Once a page/model scope merges in,
    // detectChanges() forces Angular to re-evaluate translateHeader().
    this.transloco.events$
      .pipe(
        filter(e => e.type === 'translationLoadSuccess'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.modelTranslation.onLabelsUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.detectChanges());
  }

  /**
   * Setup ResizeObserver to continuously enforce minWidths and to keep the
   * `.row-detail-float` overlay tops in sync with their spacer cells whenever
   * the table reflows (column resize, paginator change, etc.).
   */
  private setupResizeObserver(): void {
    const tableElement = this.tableContainer?.querySelector('table');
    if (!tableElement) return;

    this.resizeObserver?.disconnect();
    // Track last-seen WIDTH per observed element so we only run column-width
    // enforcement when width changes. The table's height changes on every
    // expanded-row content update (tab switch, image load, nested-table data
    // arrival) — running column enforcement on those is both wasteful and a
    // jitter source: width writes during a height-only reflow can push the
    // outer table scrollbar to appear/disappear, which then changes float
    // width, which reflows nested content, etc.
    const lastWidths = new WeakMap<Element, number>();
    this.resizeObserver = new ResizeObserver(entries => {
      let widthChanged = false;
      for (const entry of entries) {
        const w = entry.contentRect.width;
        const prev = lastWidths.get(entry.target);
        if (prev === undefined || Math.abs(prev - w) >= 0.5) {
          lastWidths.set(entry.target, w);
          widthChanged = true;
        }
      }
      if (widthChanged) {
        this.enforceAllColumnMinWidths();
      }
      this.syncViewportMetrics();
      // Spacer top recompute is cheap and idempotent: schedule it on any
      // resize so vertical reflows still keep float tops aligned.
      this.scheduleDetailFloatTopRecompute();
    });

    this.resizeObserver.observe(tableElement);
    if (this.tableWrapper) {
      this.resizeObserver.observe(this.tableWrapper);
    }
    this.syncViewportMetrics();
    this.bindViewportScroll();
  }

  private syncViewportMetrics(): void {
    const wrapper = this.tableWrapper;
    if (!wrapper) return;
    const height = wrapper.clientHeight;
    if (height !== this.viewportHeight()) {
      this.viewportHeight.set(height);
    }
    const top = wrapper.scrollTop;
    if (top !== this.viewportScrollTop()) {
      this.viewportScrollTop.set(top);
    }
  }

  private bindViewportScroll(): void {
    const wrapper = this.tableWrapper;
    if (!wrapper) return;
    wrapper.removeEventListener('scroll', this.onTableWrapperScroll);
    wrapper.addEventListener('scroll', this.onTableWrapperScroll, {
      passive: true,
    });
  }

  /**
   * Observe height changes on each `.row-detail-float`. The measured height is
   * written into `detailFloatHeights` and bound back into the empty spacer
   * `<td>` so the table reserves the right vertical space.
   *
   * Called on init and whenever the `rowDetailFloats` QueryList changes.
   */
  private setupDetailFloatObservers(): void {
    if (!this.detailFloatResizeObserver) {
      this.detailFloatResizeObserver = new ResizeObserver(entries => {
        const next = new Map(this.detailFloatHeights());
        let changed = false;
        const wrapper = this.tableWrapper;
        for (const entry of entries) {
          const target = entry.target as HTMLElement;
          const rowId = target.getAttribute('data-float-row-id');
          if (!rowId) continue;
          // Use offsetHeight (border-box) — matches what the spacer should be.
          const h = target.offsetHeight;
          if (next.get(rowId) !== h) {
            next.set(rowId, h);
            changed = true;
            if (wrapper) {
              const cell = wrapper.querySelector(
                `[data-spacer-row-id="${CSS.escape(rowId)}"]`
              ) as HTMLElement | null;
              if (cell) cell.style.height = `${h}px`;
            }
          }
        }
        if (changed) {
          this.detailFloatHeights.set(next);
          this.scheduleDetailFloatTopRecompute();
          this.cdr.markForCheck();
        }
      });
    }

    // Reattach to the current set of floats. ResizeObserver.disconnect() is a
    // simple way to avoid manually diffing; observing the same target twice is
    // harmless in modern browsers, but we want a clean slate.
    this.detailFloatResizeObserver.disconnect();
    this.rowDetailFloats?.forEach(ref => {
      this.detailFloatResizeObserver!.observe(ref.nativeElement);
    });

    // Also clean up the height map for rows that no longer have a float.
    const liveRowIds = new Set(
      this.rowDetailFloats?.map(
        ref => ref.nativeElement.getAttribute('data-float-row-id') ?? ''
      ) ?? []
    );
    const heights = this.detailFloatHeights();
    let prunedHeights: Map<string, number> | null = null;
    for (const k of heights.keys()) {
      if (!liveRowIds.has(k)) {
        if (!prunedHeights) prunedHeights = new Map(heights);
        prunedHeights.delete(k);
      }
    }
    if (prunedHeights) {
      this.detailFloatHeights.set(prunedHeights);
    }
  }

  /**
   * Coalesce top recomputation to one DOM read per animation frame; avoids
   * layout thrash when several inputs (resize, expand, animation tick) fire
   * back-to-back.
   */
  private scheduleDetailFloatTopRecompute(): void {
    if (this.detailFloatTopRafId != null) return;
    this.detailFloatTopRafId = requestAnimationFrame(() => {
      this.detailFloatTopRafId = null;
      this.recomputeDetailFloatTops();
    });
  }

  /**
   * For each in-table spacer cell, read its position relative to
   * `.table-wrapper`'s scroll content and write it into `detailFloatTops`.
   * The float overlay binds `[style.top.px]` from this map.
   *
   * Scoped to the outer `<table>` so nested mat-table-base instances (rendered
   * inside row-detail floats) do not contribute their own spacer cells here.
   */
  private recomputeDetailFloatTops(): void {
    const wrapper = this.tableWrapper;
    if (!wrapper) return;
    // Direct child path avoids matching nested tables embedded in row-detail floats.
    const tableElement = wrapper.querySelector(
      ':scope > table, :scope > .cdk-drop-list-container > table'
    ) as HTMLElement | null;
    if (!tableElement) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const scrollTop = wrapper.scrollTop;

    const next = new Map<string, number>();
    for (const row of this.detailFloatRows()) {
      const rowId = this.getRowId(row);
      const cell = tableElement.querySelector<HTMLElement>(
        `[data-spacer-row-id="${CSS.escape(rowId)}"]`
      );
      if (!cell) continue;
      const cellRect = cell.getBoundingClientRect();
      const top = cellRect.top - wrapperRect.top + scrollTop;
      next.set(rowId, top);
    }

    const prev = this.detailFloatTops();
    if (prev.size === next.size) {
      let same = true;
      for (const [k, v] of next) {
        if (prev.get(k) !== v) {
          same = false;
          break;
        }
      }
      if (same) return;
    }

    this.detailFloatTops.set(next);
    this.cdr.markForCheck();
  }

  /**
   * Row-detail hosts are capped to the scrollport. Nested tables still distribute
   * column widths like `fillContainer` (unless explicitly false), but use
   * `width: 100%` with a `min-width` from assigned columns so the grid fills the
   * panel and gains a horizontal scrollbar when columns exceed the host.
   */
  private isTableInsideRowDetail(): boolean {
    return !!this.tableContainer?.closest('.row-detail-container');
  }

  /**
   * When false, table width stays at the sum of column mins (no stretch). When true,
   * extra horizontal space is distributed across columns. Row-detail nested tables still
   * use this for column sizing, but table `width`/`min-width` are handled separately so
   * the grid fills the panel and can scroll when assigned columns exceed the host.
   */
  private fillsColumnWidthsToContainer(): boolean {
    return this.appearance().fillContainer !== false;
  }

  /**
   * Enforce minWidth on all columns to prevent shrinking below minimum
   * @param skipProportionalFill when true, do not stretch columns to fill the container (e.g. after user resize)
   */
  private enforceAllColumnMinWidths(options?: {
    skipProportionalFill?: boolean;
  }): void {
    // While dragging, skip proportional fill — ResizeObserver would otherwise expand columns.
    if (this.columnResizeActive) {
      return;
    }
    if (
      typeof document !== 'undefined' &&
      document.body.classList.contains('column-resizing')
    ) {
      return;
    }

    if (this.userColumnLayoutLocked) {
      this.syncTableOuterLayoutFromStoredWidths();
      return;
    }

    const skipProportionalFill = options?.skipProportionalFill === true;

    const allColumns = this.visibleDataColumns();
    const tableElement = this.tableContainer?.querySelector(
      'table'
    ) as HTMLElement;

    if (!tableElement) return;

    const inRowDetail = this.isTableInsideRowDetail();
    const tc = this.tableContainer?.offsetWidth || 0;
    const tw = this.tableWrapper?.clientWidth || 0;
    // In row detail, prefer the scrollport (wrapper) width — container can measure wide
    // before flex settles, which over-fills columns and removes horizontal overflow.
    const containerWidth =
      inRowDetail && tw > 0 ? Math.min(tc > 0 ? tc : tw, tw) : tc;
    if (containerWidth === 0) return;

    // Count total columns including special columns
    let totalColumnCount = allColumns.length;

    // Calculate fixed widths from special columns (select, rowNumber, expand, childToggle, actions)
    let fixedColumnsWidth = 0;

    if (this.selection().enabled) {
      fixedColumnsWidth += 48; // select column width (3rem = 48px)
      totalColumnCount++;
    }

    if (this.appearance().showRowNumbers) {
      fixedColumnsWidth += 48; // row number column width (3rem = 48px)
      totalColumnCount++;
    }

    if (this.rowExpansion().enabled && !this.rowExpansion().hideExpandColumn) {
      fixedColumnsWidth += 40; // expand column width (2.5rem = 40px)
      totalColumnCount++;
    }

    if (
      this.childRows().enabled &&
      !this.childRows().embedToggleInFirstColumn &&
      !this.childRows().hideBuiltInChildToggle
    ) {
      fixedColumnsWidth += 40; // child toggle column width (2.5rem = 40px)
      totalColumnCount++;
    }

    if (this.table().hasVisibleActions()) {
      const actionsWidth = this.getActionsColumnWidth();
      fixedColumnsWidth += parseFloat(actionsWidth) || 100;
      totalColumnCount++;
    }

    // Account for borders between columns (each column has 2px of borders: left + right = 2px)
    // But adjacent columns share borders, so total border width is roughly (totalColumnCount + 1) * 1px
    const borderWidth = (totalColumnCount + 1) * 1;

    // Calculate available width for data columns with some buffer to prevent horizontal scroll
    const availableWidthForDataColumns =
      containerWidth - fixedColumnsWidth - borderWidth - 2; // Extra 2px buffer

    let totalMinWidth = 0;
    let totalCurrentWidth = 0;
    const columnTargetWidths = new Map<string, number>();

    // First pass: calculate target widths and totals
    allColumns.forEach(col => {
      const minWidth = this.getMinWidth(col);
      const maxWidth = this.getMaxWidth(col);
      totalMinWidth += minWidth;

      // Get target width from stored, config, or minWidth
      const storedWidth = this.columnWidths.get(col.id);
      const rawConfig = col.width ? parseFloat(col.width) : NaN;
      const configWidth = Number.isFinite(rawConfig) ? rawConfig : null;
      let targetWidth = storedWidth ?? configWidth ?? minWidth;
      if (!Number.isFinite(targetWidth)) {
        targetWidth = minWidth;
      }

      // Apply min/max constraints
      targetWidth = Math.max(targetWidth, minWidth);
      if (maxWidth !== null) {
        targetWidth = Math.min(targetWidth, maxWidth);
      }

      columnTargetWidths.set(col.id, targetWidth);
      totalCurrentWidth += targetWidth;
    });

    // In row detail without a paginator, skip distributing extra width into columns — that
    // makes assigned width match the panel and hides horizontal scroll for wide min-widths.
    const fillsColumnWidths =
      this.fillsColumnWidthsToContainer() &&
      !(inRowDetail && !this.showPagination());
    if (
      fillsColumnWidths &&
      totalCurrentWidth < availableWidthForDataColumns &&
      !skipProportionalFill
    ) {
      const extraSpace = availableWidthForDataColumns - totalCurrentWidth;
      const columnsWithoutMaxWidth = allColumns.filter(
        col => this.getMaxWidth(col) === null
      );

      if (columnsWithoutMaxWidth.length > 0) {
        // Distribute extra space proportionally among columns without maxWidth
        const totalProportionalWidth = columnsWithoutMaxWidth.reduce(
          (sum, col) => sum + (columnTargetWidths.get(col.id) || 0),
          0
        );

        if (totalProportionalWidth > 0) {
          columnsWithoutMaxWidth.forEach(col => {
            const targetWidth = columnTargetWidths.get(col.id) || 0;
            const proportion = targetWidth / totalProportionalWidth;
            const additionalWidth = extraSpace * proportion;
            const newWidth = targetWidth + additionalWidth;
            columnTargetWidths.set(col.id, newWidth);
          });
        } else {
          // All columns are at minWidth, distribute evenly
          const spacePerColumn = extraSpace / columnsWithoutMaxWidth.length;
          columnsWithoutMaxWidth.forEach(col => {
            const targetWidth = columnTargetWidths.get(col.id) || 0;
            const newWidth = targetWidth + spacePerColumn;
            columnTargetWidths.set(col.id, newWidth);
          });
        }
      } else if (allColumns.length > 0) {
        // All columns have maxWidth, distribute evenly
        const spacePerColumn = extraSpace / allColumns.length;
        allColumns.forEach(col => {
          const targetWidth = columnTargetWidths.get(col.id) || 0;
          const maxWidth = this.getMaxWidth(col);
          const newWidth = targetWidth + spacePerColumn;
          // Respect maxWidth if set
          columnTargetWidths.set(
            col.id,
            maxWidth !== null ? Math.min(newWidth, maxWidth) : newWidth
          );
        });
      }
    }

    // Second pass: apply widths to cells
    allColumns.forEach(col => {
      const minWidth = this.getMinWidth(col);
      const maxWidth = this.getMaxWidth(col);
      const finalWidth = columnTargetWidths.get(col.id) || minWidth;

      // Store the computed width
      this.columnWidths.set(col.id, finalWidth);

      const headerCell = this.headerCellForColumn(col.id);
      if (headerCell) {
        headerCell.style.setProperty('min-width', `${minWidth}px`, 'important');
        if (maxWidth !== null) {
          headerCell.style.setProperty(
            'max-width',
            `${maxWidth}px`,
            'important'
          );
        }
        headerCell.style.setProperty('width', `${finalWidth}px`, 'important');
      }

      for (const cell of this.dataCellsForColumn(col.id)) {
        cell.style.setProperty('min-width', `${minWidth}px`, 'important');
        if (maxWidth !== null) {
          cell.style.setProperty('max-width', `${maxWidth}px`, 'important');
        }
        cell.style.setProperty('width', `${finalWidth}px`, 'important');
      }
    });

    const totalTableMinWidth = totalMinWidth + fixedColumnsWidth;

    let assignedDataWidth = 0;
    allColumns.forEach(col => {
      assignedDataWidth +=
        columnTargetWidths.get(col.id) || this.getMinWidth(col);
    });
    const assignedTableFloor = Math.ceil(
      assignedDataWidth + fixedColumnsWidth + borderWidth
    );
    const tableScrollMinWidth = Math.max(
      totalTableMinWidth,
      assignedTableFloor
    );

    tableElement.style.setProperty(
      'min-width',
      `${tableScrollMinWidth}px`,
      'important'
    );

    if (inRowDetail) {
      tableElement.style.setProperty('width', '100%', 'important');
    } else if (fillsColumnWidths) {
      tableElement.style.setProperty('width', '100%', 'important');
    } else {
      tableElement.style.setProperty(
        'width',
        `${totalTableMinWidth}px`,
        'important'
      );
    }

    // Column layout changes shift spacer cell positions; re-measure tops next frame.
    this.scheduleDetailFloatTopRecompute();
  }

  /**
   * Applies `columnWidths` to every data column th/td with !important so values match the map
   * after mouseup (avoids Angular [style.width] / enforce pass fighting the last drag frame).
   */
  private applyStoredColumnWidthsFromMapToDom(): void {
    if (!this.tableContainer) return;
    const allColumns = this.visibleDataColumns();
    for (const col of allColumns) {
      const minW = this.getMinWidth(col);
      const maxW = this.getMaxWidth(col);
      const stored = this.columnWidths.get(col.id);
      let w: number;
      if (stored != null && Number.isFinite(stored)) {
        w = Math.max(stored, minW);
        if (maxW !== null) {
          w = Math.min(w, maxW);
        }
      } else {
        w = minW;
      }

      const headerCell = this.headerCellForColumn(col.id);
      if (headerCell) {
        headerCell.style.setProperty('min-width', `${minW}px`, 'important');
        if (maxW !== null) {
          headerCell.style.setProperty('max-width', `${maxW}px`, 'important');
        } else {
          headerCell.style.removeProperty('max-width');
        }
        headerCell.style.setProperty('width', `${w}px`, 'important');
      }

      for (const cell of this.dataCellsForColumn(col.id)) {
        cell.style.setProperty('min-width', `${minW}px`, 'important');
        if (maxW !== null) {
          cell.style.setProperty('max-width', `${maxW}px`, 'important');
        } else {
          cell.style.removeProperty('max-width');
        }
        cell.style.setProperty('width', `${w}px`, 'important');
      }
    }
  }

  /**
   * Updates table min-width / width from the column width map only — does not touch th/td widths.
   * Used while {@link userColumnLayoutLocked} instead of full {@link enforceAllColumnMinWidths}.
   */
  private syncTableOuterLayoutFromStoredWidths(): void {
    const allColumns = this.visibleDataColumns();
    const tableElement = this.tableContainer?.querySelector(
      'table'
    ) as HTMLElement;
    if (!tableElement || allColumns.length === 0) return;

    let totalColumnCount = allColumns.length;
    let fixedColumnsWidth = 0;

    if (this.selection().enabled) {
      fixedColumnsWidth += 48;
      totalColumnCount++;
    }
    if (this.appearance().showRowNumbers) {
      fixedColumnsWidth += 48;
      totalColumnCount++;
    }
    if (this.rowExpansion().enabled && !this.rowExpansion().hideExpandColumn) {
      fixedColumnsWidth += 40;
      totalColumnCount++;
    }
    if (
      this.childRows().enabled &&
      !this.childRows().embedToggleInFirstColumn &&
      !this.childRows().hideBuiltInChildToggle
    ) {
      fixedColumnsWidth += 40;
      totalColumnCount++;
    }
    if (this.table().hasVisibleActions()) {
      const actionsWidth = this.getActionsColumnWidth();
      fixedColumnsWidth += parseFloat(actionsWidth) || 100;
      totalColumnCount++;
    }

    const borderWidth = (totalColumnCount + 1) * 1;

    let totalMinWidth = 0;
    let assignedDataWidth = 0;
    allColumns.forEach(col => {
      const minWidth = this.getMinWidth(col);
      const maxWidth = this.getMaxWidth(col);
      totalMinWidth += minWidth;

      const storedWidth = this.columnWidths.get(col.id);
      const rawConfig = col.width ? parseFloat(col.width) : NaN;
      const configWidth = Number.isFinite(rawConfig) ? rawConfig : null;
      let w = storedWidth ?? configWidth ?? minWidth;
      w = Math.max(w, minWidth);
      if (maxWidth !== null) {
        w = Math.min(w, maxWidth);
      }
      if (!Number.isFinite(w)) {
        w = minWidth;
      }
      assignedDataWidth += w;
    });

    const totalTableMinWidth = totalMinWidth + fixedColumnsWidth;
    const assignedTableFloor = Math.ceil(
      assignedDataWidth + fixedColumnsWidth + borderWidth
    );
    const tableScrollMinWidth = Math.max(
      totalTableMinWidth,
      assignedTableFloor
    );

    tableElement.style.setProperty(
      'min-width',
      `${tableScrollMinWidth}px`,
      'important'
    );

    const inRowDetail = this.isTableInsideRowDetail();
    const fillsColumnWidths =
      this.fillsColumnWidthsToContainer() &&
      !(inRowDetail && !this.showPagination());

    if (this.userColumnLayoutLocked) {
      // table-layout:fixed + width:100% leaves horizontal slack; the engine grows columns to
      // fill the table, so mouseup looks like a jump. Pin the table to the sum of column px.
      tableElement.style.setProperty(
        'width',
        `${tableScrollMinWidth}px`,
        'important'
      );
    } else if (inRowDetail) {
      tableElement.style.setProperty('width', '100%', 'important');
    } else if (fillsColumnWidths) {
      tableElement.style.setProperty('width', '100%', 'important');
    } else {
      tableElement.style.setProperty(
        'width',
        `${totalTableMinWidth}px`,
        'important'
      );
    }
  }

  ngOnDestroy(): void {
    this.cancelColumnPointerDrag();
    // Cleanup detail components
    this.detailComponents.forEach(component => component.destroy());
    this.detailComponents.clear();

    // Cleanup resize observers
    this.resizeObserver?.disconnect();
    this.detailFloatResizeObserver?.disconnect();
    this.tableWrapper?.removeEventListener('scroll', this.onTableWrapperScroll);

    if (this.detailFloatTopRafId != null) {
      cancelAnimationFrame(this.detailFloatTopRafId);
      this.detailFloatTopRafId = null;
    }

    this.paginatorPageSizeTooltipCleanup?.();
    this.clearPaginatorMatTooltips();

    this.table().onDestroy();
  }

  /**
   * Handle sort change
   */
  onSortChange(sort: Sort): void {
    this.table().setSort(sort.active, sort.direction as SortOrderEnum);
  }

  /**
   * Handle page change
   */
  onPageChange(event: PageEvent): void {
    if (event.pageSize !== this.pagination().pageSize) {
      this.table().setPageSize(event.pageSize);
    } else {
      this.table().setPage(event.pageIndex);
    }
    queueMicrotask(() => this.updatePaginatorPageSizeTooltips());
  }

  private setupPaginatorPageSizeTooltips(): void {
    this.paginatorPageSizeTooltipCleanup?.();
    this.clearPaginatorMatTooltips();

    const root = this.paginatorElement?.nativeElement;
    const select = root?.querySelector('mat-select');
    if (!root || !select) {
      return;
    }

    const onSelectClick = () => {
      requestAnimationFrame(() => this.updatePaginatorPageSizeOptionTooltips());
    };

    select.addEventListener('click', onSelectClick);
    this.paginatorPageSizeTooltipCleanup = () => {
      select.removeEventListener('click', onSelectClick);
    };

    this.updatePaginatorPageSizeTooltips();
  }

  private updatePaginatorPageSizeTooltips(): void {
    const root = this.paginatorElement?.nativeElement;
    if (!root) {
      return;
    }

    const pageSize = String(this.pagination().pageSize);
    const trigger = root.querySelector('.mat-mdc-select-trigger');
    if (trigger instanceof HTMLElement) {
      this.paginatorTriggerTooltip?.ngOnDestroy();
      this.paginatorTriggerTooltip = this.createMatTooltip(trigger, pageSize);
    }

    this.updatePaginatorPageSizeOptionTooltips();
  }

  private updatePaginatorPageSizeOptionTooltips(): void {
    this.paginatorOptionTooltips.forEach(tooltip => tooltip.ngOnDestroy());
    this.paginatorOptionTooltips.clear();

    document
      .querySelectorAll('.mat-table-paginator-page-size-panel mat-option')
      .forEach(option => {
        if (!(option instanceof HTMLElement)) {
          return;
        }

        const label = option.textContent?.trim();
        if (!label) {
          return;
        }

        this.paginatorOptionTooltips.set(
          option,
          this.createMatTooltip(option, label)
        );
      });
  }

  private createMatTooltip(element: HTMLElement, message: string): MatTooltip {
    const tooltipInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: ElementRef, useValue: new ElementRef(element) },
        MatTooltip,
      ],
    });
    const tooltip = tooltipInjector.get(MatTooltip);
    tooltip.message = message;
    tooltip.position = 'above';
    tooltip.showDelay = 0;
    tooltip.tooltipClass = 'mat-table-cell-tooltip';
    tooltip.ngAfterViewInit();
    return tooltip;
  }

  private clearPaginatorMatTooltips(): void {
    this.paginatorTriggerTooltip?.ngOnDestroy();
    this.paginatorTriggerTooltip = undefined;
    this.paginatorOptionTooltips.forEach(tooltip => tooltip.ngOnDestroy());
    this.paginatorOptionTooltips.clear();
  }

  /**
   * Returns true when the given row is allowed to open the row-detail panel.
   */
  isExpandableRow(row: T): boolean {
    return this.table().canExpandRow(row);
  }

  /**
   * Handle row click
   */
  onRowClick(row: T, event?: Event): void {
    const expansionConfig = this.rowExpansion();
    const childRowsConfig = this.childRows();
    const selectionConfig = this.selection();

    // Don't handle clicks on action buttons, radio buttons, or checkboxes.
    // Clicks on text inside a <button> report the Text node as target - it has no .closest.
    const rawTarget = event?.target;
    const el =
      rawTarget instanceof Node
        ? rawTarget.nodeType === Node.TEXT_NODE
          ? rawTarget.parentElement
          : (rawTarget as HTMLElement)
        : null;
    if (
      el?.closest('.mat-mdc-icon-button') ||
      el?.closest('button') ||
      el?.closest('a') ||
      el?.closest('mat-radio-button') ||
      el?.closest('mat-checkbox') ||
      el?.closest('.mat-table-cell--suppress-row-toggle') ||
      // Nested association tables / tab UI live in the floating row-detail;
      // never toggle the parent catalogue row from those clicks.
      el?.closest('.row-detail-float') ||
      el?.closest('.row-detail-container') ||
      el?.closest('.model-row-detail')
    ) {
      return;
    }

    // Nested tables inside an open row-detail must not bubble to the parent
    // catalogue table (which uses expandOnRowClick).
    if (this.isTableInsideRowDetail()) {
      event?.stopPropagation();
    }

    // Handle row selection (if enabled)
    if (selectionConfig.enabled) {
      this.table().toggleRowSelection(row);
      this.cdr.markForCheck();
    }

    // Handle row expansion (row details) — only for expandable rows
    if (
      expansionConfig.enabled &&
      expansionConfig.expandOnRowClick &&
      this.isExpandableRow(row)
    ) {
      this.toggleRow(row);
    }

    // Handle child rows toggle
    if (
      childRowsConfig.enabled &&
      childRowsConfig.toggleOnRowClick &&
      childRowsConfig.isParent(row)
    ) {
      this.toggleChildRows(row);
    }
  }

  /**
   * Handle selection change
   */
  onSelectionChange(row: T): void {
    // The checkbox/radio state is already updated by Material, so we just toggle
    this.table().toggleRowSelection(row);
    this.cdr.markForCheck();
  }

  /**
   * Whether all visible rows on the current page are selected (for multiple selection header)
   */
  isAllSelected(): boolean {
    const config = this.selection();
    if (!config.enabled || config.mode !== 'multiple') return false;
    const rows = this.data();
    return (
      rows.length > 0 && rows.every(row => this.table().isRowSelected(row))
    );
  }

  /**
   * Whether some but not all visible rows are selected (indeterminate header checkbox)
   */
  isSomeSelected(): boolean {
    const config = this.selection();
    if (!config.enabled || config.mode !== 'multiple') return false;
    const rows = this.data();
    const selectedCount = rows.filter(row =>
      this.table().isRowSelected(row)
    ).length;
    return selectedCount > 0 && selectedCount < rows.length;
  }

  /**
   * Handle header "select all" checkbox (multiple selection only)
   */
  onSelectAllChange(): void {
    const config = this.selection();
    if (!config.enabled || config.mode !== 'multiple') return;
    const rows = this.data();
    if (this.isAllSelected()) {
      this.table().deselectAllOnPage(rows);
    } else {
      this.table().selectAllOnPage(rows);
    }
    this.cdr.markForCheck();
  }

  /**
   * Toggle row expansion
   */
  toggleRow(row: T): void {
    // Prevent toggling if row is already closing
    if (this.closingRows().has(row)) {
      return;
    }

    const wasExpanded = this.isRowExpanded(row);

    if (wasExpanded) {
      // Collapse: keep the overlay mounted, animate max-height to 0 (spacer
      // follows via ResizeObserver), then unmount.
      this.closingRows.update(rows => {
        const newSet = new Set(rows);
        newSet.add(row);
        return newSet;
      });
      this.cdr.detectChanges();

      requestAnimationFrame(() => {
        const rowId = this.getRowId(row);
        const wrapper = this.tableWrapper?.querySelector(
          `.row-detail-wrapper[data-row-id="${CSS.escape(rowId)}"]`
        ) as HTMLElement | null;

        if (wrapper) {
          const currentHeight = wrapper.offsetHeight;
          wrapper.style.maxHeight = `${currentHeight}px`;
          this.closingRowHeights.set(row, currentHeight);
          this.cdr.detectChanges();
          void wrapper.offsetHeight;
          requestAnimationFrame(() => {
            this.closingRowHeights.set(row, 0);
            this.cdr.detectChanges();
          });
        }

        setTimeout(() => {
          this.table().toggleRow(row);
          this.destroyDetailComponent(row);
          this.closingRows.update(rows => {
            const newSet = new Set(rows);
            newSet.delete(row);
            return newSet;
          });
          this.closingRowHeights.delete(row);
          this.syncExpandedDetailRows();
          this.cdr.detectChanges();
        }, 200);
      });
    } else {
      // Expand: mount the overlay at max-height 0, then flip the expanded
      // class next frame so CSS transitions 0 → cap. Spacer height tracks
      // the overlay via ResizeObserver (rows below are pushed down).
      this.expandingRows.update(rows => {
        const next = new Set(rows);
        next.add(row);
        return next;
      });
      this.table().toggleRow(row);
      this.destroyCollapsedDetailComponents();
      this.pendingRowCreations.add(row);
      this.syncExpandedDetailRows();
      this.cdr.detectChanges();
      this.createPendingDetailComponents();
      if (this.isRowExpanded(row) && !this.detailComponents.has(row)) {
        this.createDetailComponent(row);
      }

      requestAnimationFrame(() => {
        this.expandingRows.update(rows => {
          const next = new Set(rows);
          next.delete(row);
          return next;
        });
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Check if row is expanded (includes closing rows to keep them visible for Material Table)
   */
  isRowExpanded(row: T): boolean {
    // Return true if row is expanded OR closing, so Material Table keeps rendering the detail row
    return this.table().isRowExpanded(row) || this.closingRows().has(row);
  }

  /**
   * CDK `when` predicate for the expanded-detail row def.
   * Arrow function so `this` stays bound when Material invokes it as a callback.
   */
  readonly isExpansionDetailRow = (_index: number, row: T): boolean =>
    this.isRowExpanded(row);

  /** Re-evaluate `when` so spacer rows appear/disappear with expand state. */
  private syncExpandedDetailRows(): void {
    this.matTable?.renderRows();
  }

  /**
   * Check if row detail should show expanded class (not when closing, so animation works)
   */
  isRowDetailVisible(row: T): boolean {
    // Only show expanded class if actually expanded AND not closing AND not in
    // the one-frame "expanding" gate. The gate ensures the wrapper enters the
    // DOM at max-height: 0 first, so the CSS transition fires.
    return (
      this.table().isRowExpanded(row) &&
      !this.closingRows().has(row) &&
      !this.expandingRows().has(row)
    );
  }

  /**
   * Get the height for a closing row (for animation)
   */
  getClosingRowHeight(row: T): number | null {
    return this.closingRowHeights.get(row) ?? null;
  }

  /**
   * Inline max-height for the floating row-detail wrapper.
   * Closing rows use the measured height so the collapse animation can run;
   * expanded rows cap to the remaining table viewport so tall content scrolls
   * inside the panel instead of being clipped by `.table-wrapper`.
   */
  getRowDetailMaxHeightPx(row: T): number | null {
    const closing = this.getClosingRowHeight(row);
    if (closing !== null) {
      return closing;
    }
    if (!this.isRowDetailVisible(row)) {
      return null;
    }
    return this.computeExpandedDetailMaxHeightPx(row);
  }

  private computeExpandedDetailMaxHeightPx(row: T): number {
    const viewportHeight =
      typeof window === 'undefined' ? 0 : window.innerHeight;
    const autoHeight = (this.appearance().heightMode ?? 'auto') === 'auto';
    if (autoHeight) {
      return computeExpandedRowDetailMaxHeightPx({ viewportHeight });
    }
    const wrapper = this.tableWrapper;
    if (!wrapper) {
      return computeExpandedRowDetailMaxHeightPx({ viewportHeight });
    }
    const top = this.getDetailFloatTop(row);
    return computeExpandedRowDetailMaxHeightPx({
      viewportHeight,
      remainingInWrapperPx: Math.floor(
        wrapper.clientHeight - (top - wrapper.scrollTop)
      ),
    });
  }

  /**
   * Check if a row is closing (exposed for template)
   */
  isRowClosing(row: T): boolean {
    return this.closingRows().has(row);
  }

  /**
   * Get a unique identifier for a row (for data attributes)
   */
  getRowId(row: T): string {
    // Use the row's actual ID if available for stable tracking across sorts
    if (row && typeof row === 'object' && 'id' in row) {
      return `row-${(row as any).id}`;
    }
    // Fallback to index if no ID
    const index = this.dataSource.data.indexOf(row);
    return `row-${index}`;
  }

  /**
   * Get cell value for a row and column
   */
  getCellValue(row: T, column: MatTableColumn<T>): any {
    return this.table().getCellValue(row, column);
  }

  /**
   * Tooltip text for columns with truncated content.
   */
  getCellTooltip(row: T, column: MatTableColumn<T>): string {
    const isLocationColumn =
      column.id === 'location' ||
      column.id?.toLowerCase().includes('location') ||
      column.dataPath === '_display.location' ||
      column.dataPath?.endsWith('.location') ||
      column.label?.toLowerCase().includes('location');
    const isCommentsColumn =
      column.id === 'comments' ||
      column.id === 'comment' ||
      column.id?.toLowerCase().includes('comment') ||
      column.dataPath?.endsWith('.comments') ||
      column.dataPath?.endsWith('.comment') ||
      column.label?.toLowerCase().includes('comment');
    if (!isLocationColumn && !isCommentsColumn) return '';
    const value = this.getCellValue(row, column);
    if (value == null) return '';
    return String(value).trim();
  }

  /**
   * Get cell class for a row and column
   */
  getCellClass(row: T, column: MatTableColumn<T>): string {
    if (!column.cellClass) return '';

    if (typeof column.cellClass === 'function') {
      return column.cellClass(row);
    }

    return column.cellClass;
  }

  /**
   * Get current page index for paginator
   */
  getCurrentPageIndex(): number {
    return this.table().getCurrentPageIndex();
  }

  /**
   * Check if column is sortable
   */
  isColumnSortable(column: MatTableColumn<T>): boolean {
    return column.sortable ?? false;
  }

  /**
   * Check if column is resizable
   */
  isColumnResizable(column: MatTableColumn<T>): boolean {
    return column.resizable ?? true;
  }

  /**
   * Get current column width (from stored widths or column config)
   */
  getColumnWidth(column: MatTableColumn<T>): string | undefined {
    const storedWidth = this.columnWidths.get(column.id);
    if (storedWidth != null && Number.isFinite(storedWidth)) {
      // Ensure stored width respects minWidth and maxWidth
      const minWidth = this.getMinWidth(column);
      const maxWidth = this.getMaxWidth(column);
      let width = Math.max(storedWidth, minWidth);
      if (maxWidth !== null) {
        width = Math.min(width, maxWidth);
      }
      return `${width}px`;
    }
    // If no stored width, use column width or ensure it's at least minWidth
    if (column.width) {
      const parsed = parseFloat(column.width);
      const minWidth = this.getMinWidth(column);
      const maxWidth = this.getMaxWidth(column);
      let width = Math.max(parsed, minWidth);
      if (maxWidth !== null) {
        width = Math.min(width, maxWidth);
      }
      return `${width}px`;
    }
    // If column has minWidth but no width, return minWidth to ensure table-layout: fixed works
    if (column.minWidth) {
      const minWidth = this.getMinWidth(column);
      return `${minWidth}px`;
    }
    return undefined;
  }

  /**
   * Get min width for column (for directive)
   */
  getMinWidth(column: MatTableColumn<T>): number {
    if (column.minWidth) {
      const parsed = parseFloat(column.minWidth);
      return isNaN(parsed) ? 50 : parsed;
    }
    return 50;
  }

  /**
   * Get max width for column (for directive)
   */
  getMaxWidth(column: MatTableColumn<T>): number | null {
    if (column.maxWidth) {
      const parsed = parseFloat(column.maxWidth);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  /**
   * Data columns that are actually displayed (Material omits `hidden` from {@link MatTableService#displayedColumns}).
   * Hidden defs have no th/td in the row — including them in width sums adds "ghost" pixels and the first
   * visible column absorbs the slack under table-layout:fixed.
   */
  private visibleDataColumns(): MatTableColumn<T>[] {
    return this.columns().filter(col => !col.hidden);
  }

  private headerCellForColumn(columnId: string): HTMLElement | null {
    return (
      this.tableContainer?.querySelector(
        `th${columnIdDomSelector(columnId)}`
      ) ?? null
    );
  }

  private dataCellsForColumn(columnId: string): HTMLElement[] {
    if (!this.tableContainer) return [];
    return Array.from(
      this.tableContainer.querySelectorAll<HTMLElement>(
        `td${columnIdDomSelector(columnId)}`
      )
    );
  }

  /**
   * Handle column resize start
   */
  onColumnResizeStart(columnId: string): void {
    this.columnResizeActive = true;

    // Drop stored widths for hidden column defs so they cannot inflate table totals
    const visibleIds = new Set(this.visibleDataColumns().map(c => c.id));
    for (const key of Array.from(this.columnWidths.keys())) {
      if (!visibleIds.has(key)) {
        this.columnWidths.delete(key);
      }
    }

    // Store initial widths of visible columns only (matches DOM)
    const allColumns = this.visibleDataColumns();
    this.allColumnInitialWidths.clear();

    allColumns.forEach(col => {
      const colMinWidth = this.getMinWidth(col);
      const maxWidth = this.getMaxWidth(col);

      const headerCell = this.headerCellForColumn(col.id);

      // Prefer the rendered header width: columnWidths often holds only minWidth or a stale
      // value while table-layout + fillsColumnWidths has stretched the cell — using the map
      // first would shrink every other column on first drag and dump space into the resized
      // column (huge jump). When we cannot measure (e.g. not laid out), fall back to map/config.
      const rect = headerCell?.getBoundingClientRect();
      const measured = rect && rect.width > 0 ? Math.round(rect.width) : 0;
      const fromStored = this.columnWidths.get(col.id) ?? 0;
      const fromConfig = col.width ? parseFloat(col.width) : 0;

      let widthToStore: number;
      if (measured > 0) {
        widthToStore = measured;
      } else {
        widthToStore = Math.max(fromStored, fromConfig || 0, colMinWidth);
      }
      widthToStore = Math.max(widthToStore, colMinWidth);
      if (maxWidth !== null) {
        widthToStore = Math.min(widthToStore, maxWidth);
      }

      // Store initial width for all columns (always respecting minWidth)
      this.allColumnInitialWidths.set(col.id, widthToStore);

      // Sync map to the layout snapshot so [style.width] matches imperative resize math
      this.columnWidths.set(col.id, widthToStore);

      // Immediately apply minWidth to prevent shrinking
      if (headerCell) {
        headerCell.style.setProperty(
          'min-width',
          `${colMinWidth}px`,
          'important'
        );
        if (headerCell.offsetWidth < colMinWidth) {
          headerCell.style.setProperty(
            'width',
            `${colMinWidth}px`,
            'important'
          );
        }
      }

      for (const cell of this.dataCellsForColumn(col.id)) {
        cell.style.setProperty('min-width', `${colMinWidth}px`, 'important');
        if (cell.offsetWidth < colMinWidth) {
          cell.style.setProperty('width', `${colMinWidth}px`, 'important');
        }
      }
    });

    // Store initial width for the resizing column specifically
    const column = this.columns().find(col => col.id === columnId);
    if (column) {
      const currentWidth = this.allColumnInitialWidths.get(columnId) || 0;
      this.initialResizeWidths.set(columnId, currentWidth);
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle column resize (only resize current column, don't adjust others)
   */
  onColumnResizing(columnId: string, newWidth: number): void {
    const column = this.columns().find(col => col.id === columnId);
    if (!column) return;

    // Apply min/max constraints
    const minWidth = this.getMinWidth(column);
    const maxWidth = this.getMaxWidth(column);

    let constrainedWidth = newWidth;
    if (constrainedWidth < minWidth) {
      constrainedWidth = minWidth;
    }
    if (maxWidth !== null && constrainedWidth > maxWidth) {
      constrainedWidth = maxWidth;
    }

    // Update the resizing column width
    this.columnWidths.set(columnId, constrainedWidth);

    // Apply width to ALL visible columns to prevent browser from redistributing space
    const allColumns = this.visibleDataColumns();
    allColumns.forEach(col => {
      let widthToApply: number;

      if (col.id === columnId) {
        // Use the new width for the resizing column
        widthToApply = constrainedWidth;
      } else {
        // Use the stored initial width for all other columns
        widthToApply =
          this.allColumnInitialWidths.get(col.id) ||
          this.columnWidths.get(col.id) ||
          (col.width ? parseFloat(col.width) : 0) ||
          0;
      }

      // Ensure minWidth is always respected
      const columnMinWidth = this.getMinWidth(col);
      if (widthToApply < columnMinWidth) {
        widthToApply = columnMinWidth;
      }

      // Apply to header cell
      const headerCell = this.headerCellForColumn(col.id);
      if (headerCell) {
        // Set minWidth first, then width, to ensure minWidth is respected
        headerCell.style.setProperty(
          'min-width',
          `${columnMinWidth}px`,
          'important'
        );
        headerCell.style.setProperty('width', `${widthToApply}px`, 'important');
      }

      for (const cell of this.dataCellsForColumn(col.id)) {
        cell.style.setProperty('min-width', `${columnMinWidth}px`, 'important');
        cell.style.setProperty('width', `${widthToApply}px`, 'important');
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Handle column resize end
   */
  onColumnResizeEnd(columnId: string, finalWidth: number): void {
    try {
      const column = this.columns().find(col => col.id === columnId);
      if (!column) return;

      // Apply min/max constraints
      const minWidth = this.getMinWidth(column);
      const maxWidth = this.getMaxWidth(column);

      let constrainedWidth = finalWidth;
      if (constrainedWidth < minWidth) {
        constrainedWidth = minWidth;
      }
      if (maxWidth !== null && constrainedWidth > maxWidth) {
        constrainedWidth = maxWidth;
      }

      // Store the final width
      this.columnWidths.set(columnId, constrainedWidth);

      const resizePersist = this.table().columnResizePersist();
      if (resizePersist.enabled && resizePersist.onColumnResize) {
        const originalId = this.table().getOriginalColumnId(columnId);
        resizePersist.onColumnResize(originalId, constrainedWidth);
      }

      // Clean up initial resize widths
      this.initialResizeWidths.delete(columnId);
      this.allColumnInitialWidths.clear();

      // Lock layout: full enforce + proportional fill must not run again (data refresh / RO).
      this.userColumnLayoutLocked = true;
      // Re-apply every column from the map so siblings and the handle stay pixel-stable on release.
      this.applyStoredColumnWidthsFromMapToDom();
      this.syncTableOuterLayoutFromStoredWidths();

      this.cdr.markForCheck();

      // Re-assert after Material/CD + layout in the next frames (stops the handle “creeping”)
      requestAnimationFrame(() => {
        this.applyStoredColumnWidthsFromMapToDom();
        this.syncTableOuterLayoutFromStoredWidths();
        requestAnimationFrame(() => {
          this.applyStoredColumnWidthsFromMapToDom();
          this.syncTableOuterLayoutFromStoredWidths();
          this.cdr.markForCheck();
        });
      });
    } finally {
      this.columnResizeActive = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Create detail component for expanded row
   */
  private createDetailComponent(row: T): void {
    const config = this.rowExpansion();
    if (!config.detailComponent) {
      return;
    }

    if (this.detailComponents.has(row)) {
      this.pendingRowCreations.delete(row);
      return;
    }

    // Clear existing component for this row
    this.destroyDetailComponent(row);

    if (!this.rowDetailContainers) {
      console.warn('rowDetailContainers not available');
      return;
    }

    // Find the correct container by matching the row ID from the DOM
    // This is more reliable than using index, especially after sorting
    const rowId = this.getRowId(row);
    const containers = this.rowDetailContainers.toArray();

    // Find the container that corresponds to this row by checking the DOM
    let targetContainer: ViewContainerRef | undefined;

    for (const container of containers) {
      // Get the native element (could be a comment node for ng-container)
      const nativeElement = container.element.nativeElement;

      // Navigate up to find the row-detail-wrapper
      let wrapper: HTMLElement | null = null;

      if (nativeElement.parentElement) {
        // If it's a comment node, parentElement will be the div.row-detail-container
        // Then we need to go up one more level to get to row-detail-wrapper
        let current = nativeElement.parentElement;
        while (current) {
          if (
            current.classList &&
            current.classList.contains('row-detail-wrapper')
          ) {
            wrapper = current;
            break;
          }
          current = current.parentElement;
        }
      }

      if (wrapper && wrapper.getAttribute('data-row-id') === rowId) {
        targetContainer = container;
        break;
      }
    }

    if (!targetContainer && containers.length === 1) {
      targetContainer = containers[0];
    }

    if (!targetContainer) {
      console.warn('Container not found for row:', row);
      return;
    }

    // Clear the container to remove any stale state
    targetContainer.clear();

    // Create the component in the ViewContainerRef
    const componentRef = targetContainer.createComponent(
      config.detailComponent
    );

    // Set the row and table
    componentRef.instance.row.set(row);
    componentRef.instance.table.set(this.table());

    // Force change detection on the component to ensure it picks up the new row data
    componentRef.changeDetectorRef.markForCheck();
    componentRef.changeDetectorRef.detectChanges();

    this.detailComponents.set(row, componentRef);
    this.pendingRowCreations.delete(row);
  }

  /**
   * Create detail components for any pending rows
   * Called after view has been updated
   */
  private createPendingDetailComponents(): void {
    if (this.pendingRowCreations.size === 0) {
      return;
    }

    const pendingRows = Array.from(this.pendingRowCreations);
    for (const row of pendingRows) {
      if (this.isRowExpanded(row)) {
        if (this.detailComponents.has(row)) {
          this.pendingRowCreations.delete(row);
          continue;
        }
        this.createDetailComponent(row);
      }
    }
  }

  /**
   * Destroy detail component for row
   */
  private destroyDetailComponent(row: T): void {
    const componentRef = this.detailComponents.get(row);
    if (componentRef) {
      componentRef.destroy();
      this.detailComponents.delete(row);
    }
  }

  /** Drop detail hosts for rows that are no longer expanded (e.g. single-expand switch). */
  private destroyCollapsedDetailComponents(): void {
    for (const existing of Array.from(this.detailComponents.keys())) {
      if (!this.isRowExpanded(existing)) {
        this.destroyDetailComponent(existing);
      }
    }
  }

  /**
   * Track by function for table rows
   * Uses row ID if available, otherwise falls back to index
   * This prevents Material Table from removing and re-adding rows unnecessarily
   */
  trackByIndex(index: number, row: T): any {
    // Try to use id property if it exists (most entities have an id)
    if (row && typeof row === 'object' && 'id' in row) {
      return (row as any).id;
    }
    // Fallback to index if no id is available
    return index;
  }

  /**
   * Handle filter applied
   */
  onFilterApplied(columnId: string, filter: MatTableFilter): void {
    this.table().setFilter(columnId, filter);
    // Trigger change detection to ensure the filter is applied
    this.cdr.detectChanges();
  }

  /**
   * Handle filter cleared
   */
  onFilterCleared(columnId: string): void {
    this.table().setFilter(columnId, null);
  }

  /**
   * Get current filter for a column
   */
  getColumnFilter(columnId: string): MatTableFilter | null {
    const filters = this.table().query().filters || {};
    // Use original ID for filter lookup
    const originalId = this.table().getOriginalColumnId(columnId);
    return filters[originalId] || null;
  }

  /**
   * Get row number for display
   * Accounts for pagination offset
   */
  getRowNumber(row: T): number {
    const data = this.data();
    const index = data.indexOf(row);
    if (index === -1) {
      return 0;
    }
    const query = this.table().query();
    const skip = query.skip || 0;
    return index + 1 + skip;
  }

  /**
   * Calculate actions column width based on number of actions
   * Returns width in pixels (48px per action button)
   */
  getActionsColumnWidth(): string {
    const actionsConfig = this.actions();

    if (actionsConfig.width) {
      return actionsConfig.width;
    }

    // If using a template, default to 100px (can't count actions)
    // Widen when header has content (e.g. Clear All button)
    if (actionsConfig.template) {
      return actionsConfig.headerTemplate ? '120px' : '100px';
    }

    // Calculate based on number of actions (48px per action)
    const actionCount = actionsConfig.actions?.length || 0;
    const width = actionCount * 48;

    // Minimum width of 100px
    return `${Math.max(width, 100)}px`;
  }

  /**
   * Toggle child rows visibility for a parent row
   */
  toggleChildRows(row: T): void {
    this.table().toggleChildRows(row);
  }

  /**
   * Check if child rows are visible for a parent row
   */
  areChildRowsVisible(row: T): boolean {
    return this.table().areChildRowsVisible(row);
  }

  /**
   * Check if a row is a parent (has children)
   */
  isParentRow(row: T): boolean {
    const config = this.childRows();
    return config.enabled && config.isParent(row);
  }

  /** Tree chevron in first column: parent rows or optional singleton / custom rows. */
  showChildTreeToggleIcon(row: T): boolean {
    const config = this.childRows();
    if (!config.enabled) return false;
    if (config.isParent(row)) return true;
    return config.showTreeToggleIconWhen?.(row) ?? false;
  }

  /** Chevron click: expand/collapse children, or toggle row detail for `showTreeToggleIconWhen` rows. */
  onEmbeddedTreeToggleClick(row: T): void {
    if (this.isParentRow(row)) {
      this.toggleChildRows(row);
      return;
    }
    const config = this.childRows();
    if (
      config.showTreeToggleIconWhen?.(row) &&
      this.rowExpansion().enabled &&
      this.isExpandableRow(row)
    ) {
      this.toggleRow(row);
    }
  }

  /**
   * Check if a row is a child
   */
  isChildRow(row: T): boolean {
    const config = this.childRows();
    return config.enabled && config.isChild(row);
  }

  /**
   * Get row class for parent/child styling and selection
   */
  getRowClass(row: T): string {
    const classes: string[] = [];

    // Selection highlighting: blue tint for selected, dark blue for current selection
    const selectionConfig = this.selection();
    if (
      selectionConfig.enabled &&
      selectionConfig.highlightSelected &&
      this.table().isRowSelected(row)
    ) {
      classes.push('selected-row');
      if (this.table().currentSelectionRow() === row) {
        classes.push('current-selection');
      }
    }

    // Child rows styling
    const config = this.childRows();
    if (config.enabled) {
      if (config.isParent(row)) {
        if (typeof config.parentRowClass === 'function') {
          classes.push(config.parentRowClass(row));
        } else if (config.parentRowClass) {
          classes.push(config.parentRowClass);
        }
        classes.push('parent-row');
      }

      if (config.isChild(row)) {
        if (typeof config.childRowClass === 'function') {
          classes.push(config.childRowClass(row));
        } else if (config.childRowClass) {
          classes.push(config.childRowClass);
        }
        classes.push('child-row');
      }
    }

    const customRowClass = this.appearance().getRowClass?.(row);
    if (customRowClass) {
      classes.push(customRowClass);
    }

    return classes.join(' ');
  }

  /**
   * Mat-tree-style indent for the child-toggle column (chevron shifts right per level).
   */
  getChildToggleCellStyle(row: T): Record<string, string> {
    const config = this.childRows();
    if (
      !config.enabled ||
      !config.indentChildren ||
      config.embedToggleInFirstColumn
    ) {
      return {};
    }
    const depth = this.table().getRowTreeDepth(row);
    if (depth <= 0) {
      return {};
    }
    const step = config.indentAmount ?? 24;
    return {
      paddingLeft: `${depth * step}px`,
    };
  }

  /**
   * Reserved for data-cell padding when not using embedded tree UI (currently unused).
   */
  getChildRowCellStyle(
    _row: T,

    _globalColumnIndex: number
  ): Record<string, string> {
    return {};
  }

  /** True when tree chevrons render inside the first data column (mat-tree style). */
  shouldEmbedChildToggleInCell(dataColumnLoopIndex: number): boolean {
    const config = this.childRows();
    return (
      config.enabled &&
      !!config.embedToggleInFirstColumn &&
      dataColumnLoopIndex === 0
    );
  }

  /** Left padding for embedded tree row (depth × step). */
  getEmbeddedTreeIndentStyle(row: T): Record<string, string> {
    const config = this.childRows();
    if (!config.enabled || !config.indentChildren) {
      return {};
    }
    const depth = this.table().getRowTreeDepth(row);
    const step = config.indentAmount ?? 24;
    return { paddingLeft: `${depth * step}px` };
  }

  /** Global column index for the n-th data column (0 = first data column). */
  getDataColumnGlobalIndex(dataColumnLoopIndex: number): number {
    return this.getFirstDataColumnIndex() + dataColumnLoopIndex;
  }

  /**
   * Reset scroll position to top when data changes
   * Resets both vertical and horizontal scrolling
   */
  resetScrollPosition(): void {
    this.viewportScrollTop.set(0);
    // Use setTimeout to ensure DOM is updated before resetting scroll
    setTimeout(() => {
      if (this.tableWrapper) {
        this.tableWrapper.scrollTop = 0;
        this.tableWrapper.scrollLeft = 0;
      }
      // Also reset scroll on table container if it's scrollable
      if (this.tableContainer) {
        this.tableContainer.scrollTop = 0;
        this.tableContainer.scrollLeft = 0;
      }
    }, 0);
  }

  isStripedEvenRow(row: T): boolean {
    const index = this.visibleIndexByRow().get(row);
    return index != null && index % 2 === 1;
  }

  isRenderedRow(row: T): boolean {
    return this.renderedRowSet().has(row);
  }

  /** Run change detection on this table so it re-renders after external data updates (e.g. after add documents). */
  runChangeDetection(): void {
    this.cdr.detectChanges();
  }

  /**
   * Get the index of the first data column (after special columns like select, expand, childToggle, rowNumber)
   */
  getFirstDataColumnIndex(): number {
    let index = 0;
    if (this.selection().enabled) index++;
    if (this.appearance().showRowNumbers) index++;
    if (this.rowExpansion().enabled && !this.rowExpansion().hideExpandColumn) {
      index++;
    }
    if (
      this.childRows().enabled &&
      !this.childRows().embedToggleInFirstColumn &&
      !this.childRows().hideBuiltInChildToggle
    )
      index++;
    return index;
  }

  /**
   * Handle drag-drop event for row reordering
   */
  handleDragDrop(event: CdkDragDrop<T[]>): void {
    const dragDropConfig = this.dragDrop();
    if (dragDropConfig.onDragDrop) {
      dragDropConfig.onDragDrop(event);
    }
  }

  isColumnDraggable(column: MatTableColumn<T>): boolean {
    return this.columnReorder().enabled && !column.hidden;
  }

  columnLayoutTrack(
    index: number,
    column: MatTableColumn<T> & { originalId?: string }
  ): string {
    return `${this.table().columnLayoutEpoch()}:${this.getColumnOriginalId(column)}:${index}`;
  }

  getColumnOriginalId(
    column: MatTableColumn<T> & { originalId?: string }
  ): string {
    return column.originalId ?? this.table().getOriginalColumnId(column.id);
  }

  isColumnDropHighlighted(
    column: MatTableColumn<T> & { originalId?: string }
  ): boolean {
    const highlightId = this.columnDropHighlightId();
    return !!highlightId && highlightId === this.getColumnOriginalId(column);
  }

  isColumnDragSource(column: MatTableColumn<T>): boolean {
    return this.columnDragActive() && this.columnDragSourceId() === column.id;
  }

  isColumnDragOver(column: MatTableColumn<T>): boolean {
    if (!this.columnDragActive()) return false;
    const over = this.columnDragOverIndex();
    if (over == null) return false;
    // The over-index is in preview order; find which column sits there in the preview
    return this.dragPreviewColumns()[over]?.id === column.id;
  }

  onColumnDragHandlePointerDown(
    event: PointerEvent,
    column: MatTableColumn<T> & { originalId?: string },
    columnIndex: number
  ): void {
    if (!this.isColumnDraggable(column) || event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    this.cancelColumnPointerDrag();

    this.columnDragSourceIndex.set(columnIndex);
    this.columnDragOverIndex.set(columnIndex);
    this.columnDragPointerId = event.pointerId;
    this.columnDragActive.set(true);
    document.body.classList.add('column-header-dragging');
    this.columnDragGhost.set({
      label: this.translateHeader(column.label),
      x: event.clientX,
      y: event.clientY,
    });

    // Snapshot original header positions BEFORE any preview rearrangement.
    // Using these frozen rects for the entire drag prevents the feedback loop
    // where preview DOM shifts cause resolveDataColumnIndexFromPointer to
    // return a different index, triggering another preview shift, and so on.
    if (this.tableContainer) {
      const prefix = this.table().columnIdPrefix();
      this.columnDragOriginalRects = Array.from(
        this.tableContainer.querySelectorAll<HTMLElement>('th[data-column-id]')
      )
        .filter(th =>
          (th.getAttribute('data-column-id') ?? '').startsWith(`${prefix}-`)
        )
        .map(th => {
          const r = th.getBoundingClientRect();
          return { left: r.left, right: r.right };
        });
    }
    this.columnDragScrollLeftAtStart = this.tableWrapper?.scrollLeft ?? 0;
    this.columnDragLastClientX = event.clientX;

    const onMove = (moveEvent: PointerEvent): void => {
      if (moveEvent.pointerId !== this.columnDragPointerId) return;
      this.columnDragLastClientX = moveEvent.clientX;
      this.columnDragGhost.update(ghost =>
        ghost ? { ...ghost, x: moveEvent.clientX, y: moveEvent.clientY } : ghost
      );
      const over = this.resolveDataColumnIndexFromPointer(moveEvent.clientX);
      if (over != null) {
        this.columnDragOverIndex.set(over);
      }
      this.updateColumnDragAutoScrollSpeed(moveEvent.clientX);
      this.cdr.markForCheck();
    };

    const onUp = (upEvent: PointerEvent): void => {
      if (upEvent.pointerId !== this.columnDragPointerId) return;
      this.finishColumnPointerDrag();
      cleanup();
    };

    // Manual wheel/scrollbar scrolls must also move the drop target — the
    // frozen header rects are viewport-relative, so re-resolve on scroll.
    const onWrapperScroll = (): void => {
      this.updateColumnDragOverFromLastPointer();
    };

    const cleanup = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      this.tableWrapper?.removeEventListener('scroll', onWrapperScroll);
      this.stopColumnDragAutoScroll();
      this.columnDragPointerCleanup = null;
    };

    this.columnDragPointerCleanup = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    this.tableWrapper?.addEventListener('scroll', onWrapperScroll, {
      passive: true,
    });
    this.ensureColumnDragScrollLoop();
  }

  private finishColumnPointerDrag(): void {
    const source = this.columnDragSourceIndex();
    // Use the last cached over-index rather than re-resolving from the pointer:
    // with the live preview the DOM is already rearranged, so a fresh DOM query
    // would return a preview-relative index that does not match the original order.
    const target = this.columnDragOverIndex();
    this.resetColumnDragVisuals();

    if (source == null || target == null || source === target) return;

    const config = this.columnReorder();
    if (!config.enabled) return;

    const orderedIds = this.table().reorderDataColumns(source, target);
    if (!orderedIds) return;

    const movedId =
      orderedIds[Math.min(source, target)] ?? orderedIds[0] ?? null;
    config.onReorder?.(orderedIds);
    if (movedId) {
      this.playColumnDropAnimation(movedId);
    }
    this.refreshTableColumnLayout();
  }

  private cancelColumnPointerDrag(): void {
    this.columnDragPointerCleanup?.();
    this.columnDragPointerId = null;
    this.resetColumnDragVisuals();
  }

  /**
   * Updates the horizontal auto-scroll speed while column-dragging, based on
   * the pointer's distance from the visible left/right edge of the wrapper.
   * The scrolling itself runs in a persistent rAF loop so it continues even
   * when the pointer is held still at the edge.
   */
  private updateColumnDragAutoScrollSpeed(clientX: number): void {
    const wrapper = this.tableWrapper;
    if (!wrapper) {
      this.columnDragAutoScrollSpeed = 0;
      return;
    }

    const SCROLL_ZONE = 80;
    const MAX_SPEED = 14;

    const rect = wrapper.getBoundingClientRect();
    // Clamp to the viewport: if the wrapper extends past the window edge, the
    // reachable edge is the window's, not the wrapper's layout edge.
    const left = Math.max(rect.left, 0);
    const right = Math.min(rect.right, window.innerWidth);

    let speed = 0;
    if (clientX < left + SCROLL_ZONE) {
      speed = -MAX_SPEED * (1 - Math.max(clientX - left, 0) / SCROLL_ZONE);
    } else if (clientX > right - SCROLL_ZONE) {
      speed = MAX_SPEED * (1 - Math.max(right - clientX, 0) / SCROLL_ZONE);
    }
    this.columnDragAutoScrollSpeed = speed;
  }

  /**
   * Persistent rAF loop for the whole column drag. Applies the current
   * auto-scroll speed every frame (even when the pointer isn't moving) and
   * re-resolves the drop target as columns stream past under the pointer.
   */
  private ensureColumnDragScrollLoop(): void {
    if (this.columnDragScrollRaf != null) return;

    const tick = (): void => {
      this.columnDragScrollRaf = null;
      if (!this.columnDragActive()) return;

      const wrapper = this.tableWrapper;
      const speed = this.columnDragAutoScrollSpeed;
      if (wrapper && speed !== 0) {
        const before = wrapper.scrollLeft;
        wrapper.scrollLeft += speed;
        if (wrapper.scrollLeft !== before) {
          this.updateColumnDragOverFromLastPointer();
        }
      }
      this.columnDragScrollRaf = requestAnimationFrame(tick);
    };

    this.columnDragScrollRaf = requestAnimationFrame(tick);
  }

  /** Re-resolves the drop target from the last pointer position (after scrolls). */
  private updateColumnDragOverFromLastPointer(): void {
    const clientX = this.columnDragLastClientX;
    if (clientX == null || !this.columnDragActive()) return;
    const over = this.resolveDataColumnIndexFromPointer(clientX);
    if (over != null && over !== this.columnDragOverIndex()) {
      this.columnDragOverIndex.set(over);
      this.cdr.markForCheck();
    }
  }

  private stopColumnDragAutoScroll(): void {
    this.columnDragAutoScrollSpeed = 0;
    if (this.columnDragScrollRaf != null) {
      cancelAnimationFrame(this.columnDragScrollRaf);
      this.columnDragScrollRaf = null;
    }
  }

  private resetColumnDragVisuals(): void {
    document.body.classList.remove('column-header-dragging');
    this.columnDragActive.set(false);
    this.columnDragSourceIndex.set(null);
    this.columnDragOverIndex.set(null);
    this.columnDragGhost.set(null);
    this.columnDragOriginalRects = [];
    this.columnDragLastClientX = null;
    this.columnDragScrollLeftAtStart = 0;
  }

  private resolveDataColumnIndexFromPointer(clientX: number): number | null {
    // Prefer the frozen snapshot taken at drag-start so that preview DOM
    // rearrangements don't change what index we resolve to (avoids oscillation).
    const rects = this.columnDragOriginalRects;

    if (rects.length) {
      // The snapshot is in viewport coordinates; shift it by the wrapper's
      // scroll delta so it tracks the columns' current on-screen positions.
      const wrapper = this.tableWrapper;
      const scrollDelta = wrapper
        ? wrapper.scrollLeft - this.columnDragScrollLeftAtStart
        : 0;

      for (let i = 0; i < rects.length; i++) {
        const left = rects[i]!.left - scrollDelta;
        const right = rects[i]!.right - scrollDelta;
        if (clientX >= left && clientX <= right) {
          return i;
        }
      }
      if (clientX < rects[0]!.left - scrollDelta) return 0;
      if (clientX > rects[rects.length - 1]!.right - scrollDelta)
        return rects.length - 1;
      // Pointer is in a gap between columns — find the nearest
      let closest = 0;
      let minDist = Infinity;
      for (let i = 0; i < rects.length; i++) {
        const mid = (rects[i]!.left + rects[i]!.right) / 2 - scrollDelta;
        const dist = Math.abs(clientX - mid);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      }
      return closest;
    }

    // Fallback to live DOM when no snapshot is available (e.g. non-preview path)
    if (!this.tableContainer) return null;
    const prefix = this.table().columnIdPrefix();
    const headers = Array.from(
      this.tableContainer.querySelectorAll<HTMLElement>('th[data-column-id]')
    ).filter(th =>
      (th.getAttribute('data-column-id') ?? '').startsWith(`${prefix}-`)
    );

    if (!headers.length) return null;
    for (let i = 0; i < headers.length; i++) {
      const r = headers[i]!.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) return i;
    }
    const first = headers[0]!.getBoundingClientRect();
    if (clientX < first.left) return 0;
    const last = headers[headers.length - 1]!.getBoundingClientRect();
    if (clientX > last.right) return headers.length - 1;
    return null;
  }

  private playColumnDropAnimation(columnId: string): void {
    this.columnDropHighlightId.set(columnId);
    this.columnReorderAnimating.set(true);

    window.setTimeout(() => this.columnDropHighlightId.set(null), 700);
    window.setTimeout(() => this.columnReorderAnimating.set(false), 450);
  }

  private refreshTableColumnLayout(): void {
    const rows = this.dataSource.data;
    if (rows.length) {
      this.dataSource.data = rows.slice();
    }

    this.cdr.detectChanges();
    this.matTable?.renderRows();
    this.cdr.markForCheck();

    requestAnimationFrame(() => {
      this.enforceAllColumnMinWidths({ skipProportionalFill: true });
      this.cdr.markForCheck();
    });
  }
}
